<template>

    <v-modal :title="$t('labels.create_project')" :loading="starting">
        <template #alert>
            <v-alert-upgrade feature="projects" />
        </template>
        <template #body>
            <v-alert :message="error" />
            <form @submit.prevent="createProject">
                <v-modal-section :title="$t('titles.settings')" icon="fas fa-cog">
                    <v-form-group :label="$t('labels.project_name')" :caption="$t('captions.project_name')">
                        <v-input :model="params.name" @input="params.name = $event" />
                    </v-form-group>
                    <v-form-group v-if="!space && spaces.length > 1" :label="$t('labels.space')">
                        <v-select :model="params.space_id" @input="selectSpace">
                            <option v-for="space in spaces" :key="space.id" :value="space.id">
                                <span v-if="space.agency_id">{{ space.name }} - {{ $t('labels.agency') }}</span>
                                <span v-else>{{ space.name }}</span>
                            </option>
                        </v-select>
                    </v-form-group>
                    <v-form-group>
                        <v-checkbox :label="$t('labels.include_sample_reviews')" :model="params.include_sample_reviews" @input="params.include_sample_reviews = $event" />
                    </v-form-group>
                </v-modal-section>
                <v-modal-section v-if="selectableMembers.length" :title="selectedSpace?.agency_id ? $t('labels.clients') : $t('labels.members')" icon="fas fa-people-group" collapsed>
                    <v-checkbox-group>
                        <v-checkbox v-for="m in selectableMembers"
                            :key="m.id"
                            :label="m.display_name_with_email"
                            :model="params.member_ids.indexOf(m.id) >= 0"
                            :disabled="['owner', 'super-admin'].includes(m.role.slug)"
                            @input="toggleMember(m)">
                        </v-checkbox>
                    </v-checkbox-group>
                </v-modal-section>
                <v-modal-section :title="selectedSpace?.agency_id ? $t('labels.invite_client') : $t('labels.invite_member')" icon="fas fa-envelope" collapsed>
                    <v-form-group :label="$t('labels.email_address')">
                        <v-input type="email" :model="params.email" @input="params.email = $event"  />
                    </v-form-group>
                    <v-form-group :label="$t('labels.sections')">
                        <v-checkbox-group-sections :model="params.section_ids" @input="params.section_ids = $event" />
                    </v-form-group>
                </v-modal-section>
            </form>
        </template>
        <template #footer>
            <v-button size="sm" theme="white" @click.native="cancel">{{ $t('labels.cancel') }}</v-button>
            <v-button size="sm" :loading="loading" @click.native="createProject">{{ $t('labels.create') }}</v-button>
        </template>
    </v-modal>

</template>

<script>

import UserApi from '@api/UserApi';
import Modal from '@cmp/Modal';
import ModalSection from '@cmp/ModalSection';
import CheckboxGroupSections from '@forms/CheckboxGroupSections';

export default {

    components: {
        'v-checkbox-group-sections': CheckboxGroupSections,
        'v-modal': Modal,
        'v-modal-section': ModalSection,
    },

    props: {
        space: {
            type: Object,
        },
    },

    data() {
        return {
            starting: true,
            loading: false,
            error: null,
            spaces: [],
            members: [],
            params: {
                include_sample_reviews: true,
                member_ids: [], // For adding existing members.
                section_ids: [], // For inviting member.
                space_id: null,
            },
        }
    },

    created() {
        // Assign all sections.
        this.params.section_ids = this.sections.map(s => s.id);
    },

    mounted() {
        this.getSpaces();
    },

    computed: {

        selectedSpace() {
            return this.spaces.find(s => s.id === this.params.space_id);
        },

        selectableMembers() {
            // Only show clients for agencies.
            if (this.selectedSpace?.agency_id) {
                return this.members.filter(m => !['owner', 'super-admin'].includes(m.role.slug));
            }
            return this.members;
        },

        sections() {
            return this.$store.getters.app.sections;
        },

    },

    methods: {

        getSpaces() {
            UserApi.getSpaces({ 'role_slugs': ['owner', 'super-admin'] })
                .then((response) => {
                    this.$store.commit('loading', false);
                    this.starting = false;
                    this.spaces = response.data.data;
                    if (this.space) {
                        this.params.space_id = this.space.id;
                    } else if (this.spaces.find(s => s.id === this.project.space_id)) {
                        this.params.space_id = this.project.space_id;
                    } else {
                        this.params.space_id = this.spaces[0].id;
                    }
                    this.selectSpace(this.params.space_id);
                }, (error) => {
                    this.$store.commit('loading', false);
                    this.$store.commit('setAlertWithError', error);
                });
        },

        createProject() {
            this.loading = true;
            UserApi.createProject(this.params)
                .then((response) => {
                    this.loading = false;
                    this.$store.commit('setAlertSuccess', this.$t('messages.created'));
                    this.$store.commit('setModal', null);
                    this.$router.push({ name: 'dashboard' }).catch(() => {});
                })
                .catch((error) => {
                    this.loading = false;
                    this.error = error.response.data.message;
                });
        },

        selectSpace(val) {
            this.params.space_id = val;
            this.params.member_ids = [];
            // Members.
            const space = this.spaces.find(s => s.id === val);
            if (space) {
                this.members = space.members;
                if (!space.agency_id) {
                    // Space does not belong to an agency. Include all members.
                    this.params.member_ids = space.members
                        .map(m => m.id);
                } else {
                    // Space belongs to an agency. Include owner and super admins.
                    this.params.member_ids = this.members
                        .filter(m => ['owner', 'super-admin'].includes(m.role.slug))
                        .map(m => m.id);
                }
            }
        },

        toggleMember(val) {
            const index = this.params.member_ids.findIndex(i => i === val.id);
            if (index >= 0) {
                this.params.member_ids.splice(index, 1);
            } else {
                this.params.member_ids.push(val.id);
            }
        },

        cancel() {
            this.$store.commit('setModal', null);
        },

    },

}

</script>
